import { useSelectedStudentIdContext } from "#/components/exam-results/context/studentsList/context";
import EmptyState from "#/components/exam-results/EmptyState";
import ResultsQuestion from "#/components/exam-results/question";
import QuestionsTable from "#/components/exam-results/QuestionsTable";
import Loading from "#/components/loading";
import {
  getEducationPackExamResult,
  getFindSupplementaryExamResult
} from "#/services/rest/gwt/exam-results/exam-results.api";
import type {
  EducationPackExamQuestionDetail,
  FindSupplementaryExamResult
} from "#/services/rest/gwt/exam-results/exam-results.types";
import { getModuleGroup } from "#/utils/legacyData/cache/generateHelper";
import { useBreakLG } from "@monta/design-system";
import { useParams, useSearchParams } from "@monta/router-react";
import { useEffect, useMemo, useState } from "react";
import AdditionalReportFilter from "./AdditionalReportFilter";
import {
  AdditionalContextInitialState,
  AdditionalContextProvider
} from "./context/context";
import styles from "./styles.module.scss";

const { filters: initialFilters } = AdditionalContextInitialState;

const moduleGroupFilter = (
  selectedModuleGroup?: number,
  data?: EducationPackExamQuestionDetail[] | FindSupplementaryExamResult[]
) => {
  if (!data) return [];
  if (selectedModuleGroup)
    return data.filter((item) => {
      const moduleGroupItem = getModuleGroup(item.moduleGroupId)?.id;
      return selectedModuleGroup === moduleGroupItem;
    });
  return data;
};

const AdditionalReport = () => {
  const params = useParams();
  const examTakingId = Number(params["examTakingId"]);
  const { selectedStudentId } = useSelectedStudentIdContext();
  const [searchParams] = useSearchParams();
  const isSimulator = searchParams.get("isSimulator") === "true";

  const { data, isLoading } = getEducationPackExamResult.useQuery({
    request: { body: { examTakingId: examTakingId ?? "" } },
    queryOptions: {
      enabled: isSimulator
    }
  });

  const { data: supplementaryData } = getFindSupplementaryExamResult.useQuery({
    request: {
      body: {
        examTakingDetailId: examTakingId,
        studentId: selectedStudentId
      }
    },
    queryOptions: {
      enabled: !isSimulator
    }
  });

  const isDesktop = useBreakLG();

  const [filters, setFilters] = useState(initialFilters);
  const [list, setList] = useState<
    EducationPackExamQuestionDetail[] | FindSupplementaryExamResult[]
  >([]);

  const details = useMemo(() => {
    return data && JSON.parse(data.questionDetails);
  }, [data]);

  useEffect(() => {
    const { selectedModuleGroup } = filters;
    const filteredModuleList = moduleGroupFilter(
      selectedModuleGroup,
      details || supplementaryData
    );
    if (isSimulator)
      setList(filteredModuleList as EducationPackExamQuestionDetail[]);
    else setList(filteredModuleList as FindSupplementaryExamResult[]);
  }, [details, filters, isSimulator, supplementaryData]);

  const value = useMemo(
    () => ({
      filters,
      setFilters
    }),
    [filters]
  );

  return (
    <AdditionalContextProvider value={value}>
      <AdditionalReportFilter data={details || supplementaryData} />
      <section className={styles["results__details"]}>
        <div className={styles["results__details-header"]}>
          <h2>بررسی سوال به سوال</h2>
          {list && <span>({list.length} سوال)</span>}
        </div>
        {isLoading ? (
          <Loading />
        ) : list.length > 0 ? (
          <div className={styles["results__details-question"]}>
            {!isDesktop ? (
              list.map((question) => (
                <ResultsQuestion
                  {...question}
                  key={question.questionId || question.questionID}
                />
              ))
            ) : (
              <QuestionsTable questions={list} />
            )}
          </div>
        ) : (
          <EmptyState />
        )}
      </section>
    </AdditionalContextProvider>
  );
};

export default AdditionalReport;
